import { buildFabricSwatchEndpoint, buildItemPhotoEndpoint } from '../api';
import { isNotApplicableColorVariantKey } from './colorVariantImages';
import {
  buildLocalizedShopText,
  normalizeLocalizedShopText,
  type LocalizedShopText,
} from './shopLocalization';
import { sanitizeImageUrl } from '../security';
import { applyDiscountPercentage, normalizeDiscountPercentage } from './pricing';
import {
  type ProductButtonOption,
  type ProductColorOption,
  type ProductOptionGroup,
  type ShopProduct,
} from '../mocks/shopProducts';
import {
  isAllowedSizingValue,
  normalizeSizingType,
  normalizeSizingValue,
  readPersistedSizingOptionsByType,
  type ItemSizingType,
} from './sizingConventions';
import {
  isNotApplicableFabricCode,
  normalizeFabricCodeSelections,
  type FabricCodeSelection,
  type FabricTypeOption,
} from './fabricCodes';

const CUSTOMIZABLE_STYLE_CODES = new Set([
  'DBV3311',
  'DBV6751',
  'DEB2588',
  'DEB2590',
  'DFF0260',
  'SAY1818',
  'SAW2302',
  'SAX054A',
  'SAZ1798',
  'PoloT01',
  'PoloT02',
  'PoloT03',
  'PoloT04',
]);
const SINGLE_SIZE_CATEGORIES = new Set<Exclude<Category, 'All'>>(['Hair', 'Nails', 'Makeup']);
let remoteCatalogFabricTypesByCode = new Map<string, FabricTypeOption>();

type Category = ShopProduct['category'] | 'All';
type RemoteItemPhoto = Record<string, unknown>;
type RemoteItem = Record<string, unknown>;
type ItemSubcategory = NonNullable<ShopProduct['itemSubcategory']>;
type ItemSubcategory2 = NonNullable<ShopProduct['itemSubcategory2']>;
type AccessoriesSize = NonNullable<ShopProduct['accessoriesSize']>;
const DEFAULT_ITEM_PHOTO_PREFIX = 'items';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function setRemoteCatalogFabricTypes(values: FabricTypeOption[]) {
  remoteCatalogFabricTypesByCode = new Map(
    values
      .filter((value) => value.fabricCode.trim())
      .map((value) => [value.fabricCode.trim().toLowerCase(), value])
  );
}

function hasCustomSizingOption(value: unknown) {
  return Array.isArray(value) && value.some((entry) => (
    typeof entry === 'string' && entry.trim().toLowerCase() === 'custom_sizing'
  ));
}

function getRemoteCatalogItemId(item: RemoteItem) {
  const idCandidates = [item.item_id, item.itemId, item.id];

  for (const candidate of idCandidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return '';
}

function parseJsonLikeValue(value: unknown): unknown {
  let currentValue: unknown = value;

  for (let depth = 0; depth < 3 && typeof currentValue === 'string'; depth += 1) {
    const trimmedValue = currentValue.trim().replace(/,\s*$/, '');
    if (!trimmedValue) {
      return currentValue;
    }

    const parseCandidates = [
      trimmedValue,
      trimmedValue.replace(/^"(.+)"$/s, '$1'),
      trimmedValue
        .replace(/^"(.+)"$/s, '$1')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\'),
    ];

    let hasParsed = false;

    for (const candidate of parseCandidates) {
      if (!candidate.startsWith('{') && !candidate.startsWith('[')) {
        continue;
      }

      try {
        currentValue = JSON.parse(candidate) as unknown;
        hasParsed = true;
        break;
      } catch {
        continue;
      }
    }

    if (!hasParsed) {
      return currentValue;
    }
  }

  return currentValue;
}

function getRemotePhotos(candidate: unknown): RemoteItemPhoto[] {
  const parsedCandidate = parseJsonLikeValue(candidate);
  if (!Array.isArray(parsedCandidate)) {
    return [];
  }

  return parsedCandidate.filter(isRecord) as RemoteItemPhoto[];
}

function getRemoteColorKeyedImages(candidate: unknown) {
  const parsedCandidate = parseJsonLikeValue(candidate);
  if (!isRecord(parsedCandidate)) {
    return [] as Array<{ colorName: string; photos: RemoteItemPhoto[] }>;
  }

  return Object.entries(parsedCandidate).flatMap(([colorName, photos]) => {
    if (!Array.isArray(photos)) {
      return [];
    }

    const normalizedColorName = colorName.trim();
    if (!normalizedColorName) {
      return [];
    }

    const normalizedPhotos = photos.filter(isRecord) as RemoteItemPhoto[];
    if (normalizedPhotos.length === 0) {
      return [];
    }

    const sortedPhotos = normalizedPhotos.slice().sort((left, right) => {
      const leftOrder = Number(left.sort_order ?? left.sortOrder ?? 0);
      const rightOrder = Number(right.sort_order ?? right.sortOrder ?? 0);
      if (Number.isFinite(leftOrder) && Number.isFinite(rightOrder) && leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      return 0;
    });

    return [{
      colorName: normalizedColorName,
      photos: sortedPhotos,
    }];
  });
}

function isPrimaryRemotePhoto(photo: RemoteItemPhoto) {
  return photo.is_primary === true || photo.isPrimary === true;
}

function sortRemotePhotosWithPrimaryFirst(photos: RemoteItemPhoto[]) {
  return photos.slice().sort((left, right) => {
    const leftIsPrimary = isPrimaryRemotePhoto(left);
    const rightIsPrimary = isPrimaryRemotePhoto(right);
    if (leftIsPrimary !== rightIsPrimary) {
      return Number(rightIsPrimary) - Number(leftIsPrimary);
    }

    const leftOrder = Number(left.sort_order ?? left.sortOrder ?? Number.MAX_SAFE_INTEGER);
    const rightOrder = Number(right.sort_order ?? right.sortOrder ?? Number.MAX_SAFE_INTEGER);
    if (Number.isFinite(leftOrder) && Number.isFinite(rightOrder) && leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return 0;
  });
}

function dedupeImageUrls(images: string[]) {
  const seen = new Set<string>();

  return images.filter((image) => {
    const normalizedImage = image.trim();
    if (!normalizedImage || seen.has(normalizedImage)) {
      return false;
    }

    seen.add(normalizedImage);
    return true;
  });
}

function resolveRemotePhotoUrl(photo: RemoteItemPhoto) {
  const directUrl = sanitizeImageUrl(
    photo.img_data ??
    photo.url ??
    photo.image_url ??
    photo.imageUrl ??
    photo.photo_url ??
    photo.photoUrl,
    false,
    true
  );

  if (directUrl) {
    return directUrl;
  }

  const objectKey = extractItemPhotoObjectKey(photo);
  return objectKey ? buildItemPhotoEndpoint(objectKey) : '';
}

function getRemotePhotoUrls(candidate: unknown) {
  return dedupeImageUrls(
    sortRemotePhotosWithPrimaryFirst(getRemotePhotos(candidate))
      .map((photo) => resolveRemotePhotoUrl(photo))
      .filter(Boolean)
  );
}

function normalizeColorMatchToken(value: unknown) {
  const normalizedHex = normalizeColorHex(value);
  if (normalizedHex) {
    return normalizedHex.toLowerCase();
  }

  if (typeof value !== 'string' && typeof value !== 'number') {
    return '';
  }

  return String(value)
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function collectColorMatchTokens(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => collectColorMatchTokens(entry));
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const token = normalizeColorMatchToken(value);
    return token ? [token] : [];
  }

  if (!isRecord(value)) {
    return [];
  }

  const labelValues = isRecord(value.label)
    ? [value.label.en, value.label.zh]
    : [value.label];
  const candidates = [
    value.code,
    value.hex,
    value.color,
    value.color_code,
    value.colorCode,
    value.color_name,
    value.colorName,
    value.color_hex,
    value.colorHex,
    value.name,
    value.title,
    ...labelValues,
  ];

  return candidates.flatMap((candidate) => collectColorMatchTokens(candidate));
}

function isDirectRemoteItemRecord(value: unknown) {
  if (!isRecord(value)) {
    return false;
  }

  return Boolean(
    value.id !== undefined
    && (
      value.title !== undefined
      || value.name !== undefined
      || value.category !== undefined
      || value.base_price !== undefined
      || value.basePrice !== undefined
      || value.price !== undefined
      || value.discount !== undefined
      || value.images !== undefined
      || value.photos !== undefined
      || value.item_photos !== undefined
      || value.itemPhotos !== undefined
    )
  );
}

export function extractRemoteItems(payload: unknown): RemoteItem[] {
  const parsedPayload = parseJsonLikeValue(payload);

  if (Array.isArray(parsedPayload)) {
    return parsedPayload.filter(isRecord);
  }

  if (!isRecord(parsedPayload)) {
    return [];
  }

  const payloadRecord = parsedPayload as Record<string, unknown>;

  if (isDirectRemoteItemRecord(payloadRecord)) {
    return [payloadRecord];
  }

  const directNestedItems = Object.values(payloadRecord).filter(isDirectRemoteItemRecord) as RemoteItem[];
  if (directNestedItems.length > 0) {
    return directNestedItems;
  }

  const candidates = [
    payloadRecord.body,
    payloadRecord.message,
    payloadRecord.item,
    payloadRecord.record,
    payloadRecord.items,
    payloadRecord.results,
    payloadRecord.data,
    payloadRecord.records,
    payloadRecord.rows,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.filter(isRecord);
    }

    if (isRecord(candidate)) {
      const nestedArray = extractRemoteItems(candidate);
      if (nestedArray.length > 0) {
        return nestedArray;
      }
    }
  }

  return [];
}

function normalizeRemoteCategory(value: unknown): Exclude<Category, 'All'> | null {
  if (typeof value !== 'string') {
    return null;
  }

  switch (value.trim().toLowerCase()) {
    case 'blazer':
    case 'blazers':
    case 'suit':
    case 'suits':
      return 'Tops';
    case 'set':
    case 'sets':
    case 'ensemble':
      return 'Sets';
    case 'tuxedo':
    case 'tuxedos':
    case 'Tuxedo/Formal Wear'.toLowerCase():
    case 'formal wear':
    case 'formalwear':
      return 'Tuxedo';
    case 'bottoms':
    case 'pants':
      return 'Pants';
    case 'short':
    case 'shorts':
      return 'Shorts';
    case 'shoe':
    case 'shoes':
    case 'footwear':
      return 'Shoes';
    case 'dress':
    case 'dresses':
      return 'Dresses';
    case 'hat':
    case 'hats':
      return 'Hats';
    case 'outerwear':
      return 'Outerwear';
    case 'accessories':
    case 'accessory':
      return 'Accessories';
    case 'hair':
    case 'hairstyle':
    case 'hairstyles':
      return 'Hair';
    case 'nail':
    case 'nails':
      return 'Nails';
    case 'makeup':
    case 'make up':
    case 'make-up':
    case 'cosmetic':
    case 'cosmetics':
      return 'Makeup';
    case 'shirt':
    case 'top':
    case 'tops':
      return 'Tops';
    default:
      return null;
  }
}

function normalizeRemoteAudience(
  value: unknown,
  category: Exclude<Category, 'All'>
): ShopProduct['audience'] {
  if (SINGLE_SIZE_CATEGORIES.has(category)) {
    return 'Accessories';
  }

  if (typeof value !== 'string') {
    return 'Women';
  }

  switch (value.trim().toLowerCase().replace(/['’\s_-]+/g, '')) {
    case 'male':
    case 'men':
    case 'man':
    case 'mens':
      return 'Men';
    case 'female':
    case 'women':
    case 'woman':
    case 'womens':
      return 'Women';
    case 'unisex':
      return 'Unisex';
    case 'accessories':
    case 'accessory':
      return 'Accessories';
    default:
      return 'Women';
  }
}

function getRemoteAudienceValue(item: RemoteItem) {
  const audienceCandidates: unknown[] = [
    item.gender,
    item.audience,
  ];

  if (isRecord(item.item)) {
    audienceCandidates.push(item.item.gender, item.item.audience);
  }

  if (isRecord(item.data)) {
    audienceCandidates.push(item.data.gender, item.data.audience);
  }

  return audienceCandidates.find((candidate) => typeof candidate === 'string' && candidate.trim());
}

function getNestedItemField(item: RemoteItem, key: string): unknown {
  const directValue = item[key];
  if (directValue !== undefined) {
    return directValue;
  }

  if (isRecord(item.item) && item.item[key] !== undefined) {
    return item.item[key];
  }

  if (isRecord(item.data) && item.data[key] !== undefined) {
    return item.data[key];
  }

  return undefined;
}

function hasTimestampValue(value: unknown) {
  if (value === null || value === undefined || value === false) {
    return false;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    return Boolean(normalizedValue && normalizedValue !== 'null' && normalizedValue !== 'undefined');
  }

  return true;
}

export function isRemoteItemDeleted(item: RemoteItem) {
  return hasTimestampValue(
    getNestedItemField(item, 'deleted_at') ?? getNestedItemField(item, 'deletedAt')
  );
}

function getItemPhotoPrefix(prefixValue = DEFAULT_ITEM_PHOTO_PREFIX) {
  const normalizedPrefix = prefixValue.trim().replace(/^\/+|\/+$/g, '') || DEFAULT_ITEM_PHOTO_PREFIX;
  return `${normalizedPrefix}/`;
}

function normalizeObjectKeyCandidate(value: string) {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return '';
  }

  const withoutLeadingSlash = trimmedValue.replace(/^\/+/, '');
  const itemPrefixIndex = withoutLeadingSlash.indexOf(getItemPhotoPrefix());

  if (itemPrefixIndex >= 0) {
    return withoutLeadingSlash.slice(itemPrefixIndex);
  }

  return withoutLeadingSlash;
}

function extractObjectKeyFromPhotoUrl(value: unknown) {
  const directUrl = sanitizeImageUrl(value);
  if (!directUrl) {
    return '';
  }

  try {
    const parsedUrl = new URL(directUrl);
    const queryObjectKey = parsedUrl.searchParams.get('objectKey');
    if (queryObjectKey) {
      return normalizeObjectKeyCandidate(decodeURIComponent(queryObjectKey));
    }

    return normalizeObjectKeyCandidate(decodeURIComponent(parsedUrl.pathname));
  } catch {
    return '';
  }
}

function extractItemPhotoObjectKey(photo: RemoteItemPhoto) {
  const directObjectKeyCandidates = [photo.s3_key, photo.s3Key, photo.objectKey];

  for (const candidate of directObjectKeyCandidates) {
    if (typeof candidate !== 'string') {
      continue;
    }

    const normalizedObjectKey = normalizeObjectKeyCandidate(candidate);
    if (normalizedObjectKey) {
      return normalizedObjectKey;
    }
  }

  return (
    extractObjectKeyFromPhotoUrl(photo.img_data)
    || extractObjectKeyFromPhotoUrl(photo.url)
    || extractObjectKeyFromPhotoUrl(photo.image_url)
    || extractObjectKeyFromPhotoUrl(photo.imageUrl)
    || extractObjectKeyFromPhotoUrl(photo.photo_url)
    || extractObjectKeyFromPhotoUrl(photo.photoUrl)
  );
}

function normalizeItemSubcategory(value: unknown): ItemSubcategory | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

  return normalizedValue ? normalizedValue : undefined;
}

function normalizeItemSubcategory2(value: unknown): ItemSubcategory2 | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

  return normalizedValue ? normalizedValue : undefined;
}

function normalizeAccessoriesSize(value: unknown): AccessoriesSize | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  switch (value.trim().toUpperCase()) {
    case 'XS':
    case 'S':
    case 'M':
    case 'L':
    case 'XL':
    case 'XXL':
      return value.trim().toUpperCase() as AccessoriesSize;
    default:
      return undefined;
  }
}

function getPrimaryImageUrl(item: RemoteItem) {
  const colorImageCandidates = [item.images, item.image_map, item.imageMap];

  for (const candidate of colorImageCandidates) {
    const colorGroups = getRemoteColorKeyedImages(candidate);
    const primaryPhoto = colorGroups[0]?.photos[0];
    if (!primaryPhoto) {
      continue;
    }

    const directUrl = sanitizeImageUrl(
      primaryPhoto.img_data ??
      primaryPhoto.url ??
      primaryPhoto.image_url ??
      primaryPhoto.imageUrl ??
      primaryPhoto.photo_url ??
      primaryPhoto.photoUrl,
      false,
      true
    );

    if (directUrl) {
      return directUrl;
    }

    const objectKey = extractItemPhotoObjectKey(primaryPhoto);
    if (objectKey) {
      return buildItemPhotoEndpoint(objectKey);
    }
  }

  const photoCandidates = [item.item_photos, item.itemPhotos, item.photos];

  for (const candidate of photoCandidates) {
    const photos = getRemotePhotos(candidate);
    const primaryPhoto =
      photos.find((photo) => photo.is_primary === true || photo.isPrimary === true) ?? photos[0];

    if (!primaryPhoto) {
      continue;
    }

    const directUrl = sanitizeImageUrl(
      primaryPhoto.url ??
      primaryPhoto.image_url ??
      primaryPhoto.imageUrl ??
      primaryPhoto.photo_url ??
      primaryPhoto.photoUrl
    );

    if (directUrl) {
      return directUrl;
    }

    const objectKey = extractItemPhotoObjectKey(primaryPhoto);

    if (objectKey) {
      return buildItemPhotoEndpoint(objectKey);
    }
  }

  const directItemImage = sanitizeImageUrl(
    item.image ?? item.image_url ?? item.imageUrl ?? item.photo_url ?? item.photoUrl
  );
  if (directItemImage) {
    return directItemImage;
  }

  for (const inventoryRow of getInventoryRows(item)) {
    const inventoryImage = getInventoryRowPhotoUrls(inventoryRow)[0];
    if (inventoryImage) {
      return inventoryImage;
    }
  }

  return '';
}

function getRemotePhotoPayload(item: RemoteItem) {
  const photoCandidates = [item.photos, item.item_photos, item.itemPhotos];

  for (const candidate of photoCandidates) {
    const photos = getRemotePhotos(candidate);
    if (photos.length > 0) {
      return photos
        .map((photo, index) => {
          const objectKey = extractItemPhotoObjectKey(photo);
          if (!objectKey) {
            return null;
          }

          return {
            s3_key: objectKey,
            is_primary: photo.is_primary === true || photo.isPrimary === true || index === 0,
          };
        })
        .filter((photo): photo is { s3_key: string; is_primary: boolean } => photo !== null);
    }
  }

  return [];
}

function getRemoteImagesPayload(item: RemoteItem) {
  const imageCandidates = [item.images, item.image_map, item.imageMap];

  for (const candidate of imageCandidates) {
    const colorGroups = getRemoteColorKeyedImages(candidate);
    if (colorGroups.length === 0) {
      continue;
    }

    return Object.fromEntries(colorGroups.map((group) => [
      group.colorName,
      group.photos.map((photo, index) => ({
        img_data: sanitizeImageUrl(
          photo.img_data ??
          photo.url ??
          photo.image_url ??
          photo.imageUrl ??
          photo.photo_url ??
          photo.photoUrl,
          false,
          true
        ) || (
          (() => {
            const objectKey = extractItemPhotoObjectKey(photo);
            return objectKey ? buildItemPhotoEndpoint(objectKey) : '';
          })()
        ),
        sort_order: Number(photo.sort_order ?? photo.sortOrder ?? index + 1) || (index + 1),
      })),
    ]));
  }

  return undefined;
}

function getStringField(item: RemoteItem, key: string) {
  const value = getNestedItemField(item, key);
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeCustomOptions(value: unknown) {
  const parsedValue = parseJsonLikeValue(value);
  if (!Array.isArray(parsedValue)) {
    return undefined;
  }

  const normalizedOptions = Array.from(
    new Set(
      parsedValue
        .filter((entry): entry is string => typeof entry === 'string')
        .map((entry) => entry.trim())
        .filter(Boolean)
    )
  );

  return normalizedOptions;
}

function getNormalizedCustomOptions(item: RemoteItem) {
  return (
    normalizeCustomOptions(getNestedItemField(item, 'custom_options'))
    ?? normalizeCustomOptions(getNestedItemField(item, 'custion_options'))
  );
}

function normalizeColorHex(value: unknown) {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmedValue = value.trim().toUpperCase();
  if (!trimmedValue) {
    return '';
  }

  const normalizedValue = trimmedValue.startsWith('#') ? trimmedValue : `#${trimmedValue}`;
  return /^#[0-9A-F]{6}$/.test(normalizedValue) ? normalizedValue : '';
}

const PLACEHOLDER_COLOR_HEX = '#D9D9D9';

function getRemoteColorVariantRecords(item: RemoteItem) {
  const colorVariantCandidates: unknown[] = [
    getNestedItemField(item, 'color_variants'),
    getNestedItemField(item, 'colorVariants'),
    getNestedItemField(item, 'color_options'),
    getNestedItemField(item, 'colorOptions'),
    getNestedItemField(item, 'colors'),
  ];

  for (const candidate of colorVariantCandidates) {
    const parsedCandidate = parseJsonLikeValue(candidate);
    if (Array.isArray(parsedCandidate)) {
      const variantRecords = parsedCandidate.filter(isRecord);
      if (variantRecords.length > 0) {
        return variantRecords;
      }
    } else if (isRecord(parsedCandidate)) {
      return Object.entries(parsedCandidate).map(([colorCode, value]): Record<string, unknown> => {
        const record = isRecord(value) ? { ...value } : {};
        record.color_code = String(record.color_code ?? record.colorCode ?? colorCode);
        return record;
      });
    }
  }

  return [];
}

function getInventoryRows(item: RemoteItem) {
  const inventoryRowsCandidate = parseJsonLikeValue(item.inventory_rows ?? item.inventoryRows);
  if (Array.isArray(inventoryRowsCandidate)) {
    return inventoryRowsCandidate.filter(isRecord);
  }

  return [];
}

function getRemoteItemPrices(item: RemoteItem) {
  const variantPrices = getRemoteColorVariantRecords(item)
    .map((variant) => Number(variant.price))
    .filter((price) => Number.isFinite(price) && price > 0);
  const inventoryPrices = getInventoryRows(item)
    .map((row) => Number(row.price ?? row.base_price ?? row.basePrice))
    .filter((price) => Number.isFinite(price) && price > 0);

  return [...variantPrices, ...inventoryPrices];
}

function getInventoryRowFabricCodeSelections(item: RemoteItem): FabricCodeSelection[] {
  // New add_items format: each fabric is its own inventory row carrying a singular
  // `fabric_code` (and its own color). Group rows by fabric, collecting applicable colors.
  const selectionsByFabric = new Map<string, { fabricCode: string; colorCodes: string[] }>();

  getInventoryRows(item).forEach((row) => {
    const fabricCode = String(row.fabric_code ?? row.fabricCode ?? '').trim();
    if (!fabricCode || isNotApplicableFabricCode(fabricCode)) {
      return;
    }

    const fabricKey = fabricCode.toLowerCase();
    const entry = selectionsByFabric.get(fabricKey) ?? { fabricCode, colorCodes: [] };

    const rowColorCode = String(row.color_code ?? row.colorCode ?? '').trim();
    if (
      rowColorCode
      && !isNotApplicableColorVariantKey(rowColorCode)
      && !entry.colorCodes.some((code) => code.toLowerCase() === rowColorCode.toLowerCase())
    ) {
      entry.colorCodes.push(rowColorCode);
    }

    selectionsByFabric.set(fabricKey, entry);
  });

  return Array.from(selectionsByFabric.values()).map(({ fabricCode, colorCodes }) => (
    [fabricCode, colorCodes] as FabricCodeSelection
  ));
}

function getRemoteFabricCodeSelections(item: RemoteItem): FabricCodeSelection[] {
  const candidates = [
    getNestedItemField(item, 'fabric_codes'),
    getNestedItemField(item, 'fabricCodes'),
    ...getInventoryRows(item).map((row) => row.fabric_codes ?? row.fabricCodes),
  ];

  for (const candidate of candidates) {
    const selections = normalizeFabricCodeSelections(candidate);
    if (selections.length > 0) {
      return selections;
    }
  }

  return getInventoryRowFabricCodeSelections(item);
}

function buildRemoteFabricOptions(item: RemoteItem): ProductButtonOption[] {
  return getRemoteFabricCodeSelections(item).map(([fabricCode]) => {
    const fabricType = remoteCatalogFabricTypesByCode.get(fabricCode.toLowerCase());
    const label = fabricType?.fabricBlendTranslations || fabricType?.fabricBlend || fabricCode;

    return {
      value: fabricCode,
      label,
    };
  });
}

function isRemoteColorVariantsNotApplicable(item: RemoteItem) {
  const explicitFlag = getNestedItemField(item, 'color_variants_not_applicable')
    ?? getNestedItemField(item, 'colorVariantsNotApplicable');
  if (typeof explicitFlag === 'boolean') {
    return explicitFlag;
  }

  const colorVariantRecords = getRemoteColorVariantRecords(item);
  if (
    colorVariantRecords.length > 0
    && colorVariantRecords.some((variant) => {
      const colorName = String(variant.color_name ?? variant.colorName ?? '').trim();
      const colorCode = String(variant.color_code ?? variant.colorCode ?? '').trim();
      return !colorName && !colorCode;
    })
  ) {
    return true;
  }

  const imagesPayload = getNestedItemField(item, 'images');
  if (isRecord(imagesPayload)) {
    const imageColorKeys = Object.keys(imagesPayload);
    const hasNotApplicableImageKey = imageColorKeys.some((key) => isNotApplicableColorVariantKey(key));
    const hasApplicableImageKey = imageColorKeys.some((key) => !isNotApplicableColorVariantKey(key));
    // Only treat the item as color-less when every image key is not-applicable. In the new
    // add_items format a fabric-only row contributes a `not_applicable` key alongside real colors.
    if (hasNotApplicableImageKey && !hasApplicableImageKey) {
      return true;
    }
  }

  return false;
}

function normalizeColorVariantLabel(variant: Record<string, unknown>) {
  const localizedLabel = normalizeLocalizedShopText(
    variant.label
    ?? variant.title
    ?? variant.name
    ?? variant.color_name
    ?? variant.colorName
    ?? variant.color
  );

  if (localizedLabel) {
    return localizedLabel;
  }

  const fallbackHex = normalizeColorHex(
    variant.hex
    ?? variant.hex_code
    ?? variant.hexCode
    ?? variant.color_hex
    ?? variant.colorHex
    ?? variant.swatch_hex
    ?? variant.swatchHex
  );

  return fallbackHex ? buildLocalizedShopText(fallbackHex, fallbackHex) : null;
}

function resolveRemoteSwatchImageUrl(value: unknown) {
  const directUrl = sanitizeImageUrl(value, false, true);
  if (directUrl) {
    return directUrl;
  }

  if (typeof value !== 'string') {
    return '';
  }

  const objectKey = normalizeObjectKeyCandidate(value);
  return objectKey ? buildFabricSwatchEndpoint(objectKey) : '';
}

function getInventoryRowPhotoUrls(inventoryRow: RemoteItem) {
  const nestedPhotoCandidates = [
    inventoryRow.s3_keys,
    inventoryRow.s3Keys,
    inventoryRow.photos,
    inventoryRow.item_photos,
    inventoryRow.itemPhotos,
  ];

  for (const candidate of nestedPhotoCandidates) {
    const photoUrls = getRemotePhotoUrls(candidate);
    if (photoUrls.length > 0) {
      return photoUrls;
    }
  }

  const directPhotoUrl = resolveRemotePhotoUrl(inventoryRow);
  return directPhotoUrl ? [directPhotoUrl] : [];
}

function getInventoryRowPhotoKeys(inventoryRow: RemoteItem) {
  const nestedPhotoCandidates = [
    inventoryRow.s3_keys,
    inventoryRow.s3Keys,
    inventoryRow.photos,
    inventoryRow.item_photos,
    inventoryRow.itemPhotos,
  ];

  for (const candidate of nestedPhotoCandidates) {
    const photoKeys = getRemotePhotos(candidate)
      .map((photo) => extractItemPhotoObjectKey(photo))
      .filter(Boolean) as string[];
    if (photoKeys.length > 0) {
      return photoKeys;
    }
  }

  return [];
}

function buildRemoteColorImageMap(item: RemoteItem) {
  const colorImageMap = new Map<string, string[]>();

  const appendImages = (tokens: string[], images: string[]) => {
    if (tokens.length === 0 || images.length === 0) {
      return;
    }

    const nextTokens = Array.from(new Set(tokens));
    nextTokens.forEach((token) => {
      const existingImages = colorImageMap.get(token) ?? [];
      colorImageMap.set(token, dedupeImageUrls([...existingImages, ...images]));
    });
  };

  [item.images, item.image_map, item.imageMap].forEach((candidate) => {
    getRemoteColorKeyedImages(candidate).forEach((group) => {
      appendImages(
        collectColorMatchTokens(group.colorName),
        dedupeImageUrls(
          sortRemotePhotosWithPrimaryFirst(group.photos)
            .map((photo) => resolveRemotePhotoUrl(photo))
            .filter(Boolean)
        )
      );
    });
  });

  getInventoryRows(item).forEach((inventoryRow) => {
    appendImages(
      Array.from(new Set([
        ...collectColorMatchTokens(inventoryRow.color),
        ...collectColorMatchTokens(inventoryRow.color_code),
        ...collectColorMatchTokens(inventoryRow.colorCode),
        ...collectColorMatchTokens(inventoryRow.color_name),
        ...collectColorMatchTokens(inventoryRow.colorName),
        ...collectColorMatchTokens(inventoryRow.color_hex),
        ...collectColorMatchTokens(inventoryRow.colorHex),
      ])),
      getInventoryRowPhotoUrls(inventoryRow)
    );
  });

  return colorImageMap.size > 0 ? Object.fromEntries(colorImageMap) : undefined;
}

function sanitizeRemoteInventoryId(value: unknown) {
  const candidate = String(value ?? '').trim();
  return candidate && !candidate.includes('/') && !candidate.includes('\\') ? candidate : '';
}

function getRemoteRowSizeValue(row: Record<string, unknown>, category?: Exclude<Category, 'All'>) {
  const candidates = [
    row.size_value,
    row.sizeValue,
    row.accessories_size,
    row.accessoriesSize,
    row.size,
    ...(category === 'Pants' || category === 'Shorts' ? [row.pant_waist, row.waist] : []),
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const value = normalizeSizingValue(candidate);
      if (value) {
        return value;
      }
    }

    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return String(candidate);
    }
  }

  return '';
}

function getPresentRemoteRowSizes(rows: Array<Record<string, unknown>>, category?: Exclude<Category, 'All'>) {
  return rows
    .map((row) => getRemoteRowSizeValue(row, category))
    .filter((value) => isAllowedSizingValue(value));
}

function inferRemoteBottomSizeSystem(item: RemoteItem, category: Exclude<Category, 'All'>) {
  if (category !== 'Pants' && category !== 'Shorts') {
    return '';
  }

  const itemSubcategory = String(
    getNestedItemField(item, 'item_subcategory')
    ?? getNestedItemField(item, 'item_subcategory1')
    ?? ''
  ).trim().toLowerCase();
  const searchableItemText = JSON.stringify([
    itemSubcategory,
    getNestedItemField(item, 'title'),
    getNestedItemField(item, 'name'),
  ]).toLowerCase();

  if (category === 'Shorts' || itemSubcategory === 'trousers' || searchableItemText.includes('trouser')) {
    return 'waist_numeric';
  }

  if (itemSubcategory === 'pants' || searchableItemText.includes('pant')) {
    return 'waist_length';
  }

  return '';
}

function buildRemoteColorOptions(item: RemoteItem): ProductColorOption[] {
  const colorVariantMetadata = getRemoteColorVariantRecords(item);
  const colorVariantRecords = [...colorVariantMetadata, ...getInventoryRows(item)];
  const groupedOptions = new Map<string, {
    option: ProductColorOption;
    inventoryIds: string[];
    sortOrder: number;
  }>();

  const swatchUrlByColorName = new Map<string, string>();
  colorVariantMetadata.forEach((variant) => {
    const colorName = String(variant.color_name ?? variant.colorName ?? variant.color ?? '').trim().toLowerCase();
    if (!colorName || isNotApplicableColorVariantKey(colorName)) {
      return;
    }

    const swatchImageUrl = resolveRemoteSwatchImageUrl(variant.colorSwatchImg ?? variant.color_swatch_img);
    if (swatchImageUrl) {
      swatchUrlByColorName.set(colorName, swatchImageUrl);
    }
  });

  const imagesPayload = getNestedItemField(item, 'images');
  if (imagesPayload && typeof imagesPayload === 'object' && !Array.isArray(imagesPayload)) {
    Object.entries(imagesPayload as Record<string, unknown>).forEach(([colorName, photos]) => {
      const normalizedColorName = colorName.trim().toLowerCase();
      if (!Array.isArray(photos) || isNotApplicableColorVariantKey(normalizedColorName) || swatchUrlByColorName.has(normalizedColorName)) {
        return;
      }

      const sorted = [...photos]
        .filter((p): p is Record<string, unknown> => p !== null && typeof p === 'object')
        .sort((a, b) => Number(a.sort_order ?? a.sortOrder ?? 0) - Number(b.sort_order ?? b.sortOrder ?? 0));
      const swatchPhoto = sorted[0];
      if (swatchPhoto) {
        const swatchImageUrl = resolveRemoteSwatchImageUrl(
          swatchPhoto.img_data
          ?? swatchPhoto.url
          ?? swatchPhoto.image_url
          ?? swatchPhoto.imageUrl
          ?? swatchPhoto.photo_url
          ?? swatchPhoto.photoUrl
          ?? swatchPhoto.s3_key
          ?? swatchPhoto.s3Key
          ?? swatchPhoto.objectKey
        );
        if (swatchImageUrl) {
          swatchUrlByColorName.set(normalizedColorName, swatchImageUrl);
        }
      }
    });
  }

  colorVariantRecords.forEach((variant) => {
    // Skip "not applicable" entries regardless of which field carries the value
    const rawColorName = String(
      variant.color_name ?? variant.colorName ?? variant.color ?? variant.label ?? variant.name ?? ''
    ).trim();
    if (isNotApplicableColorVariantKey(rawColorName)) {
      return;
    }

    const hex = normalizeColorHex(
      variant.hex
      ?? variant.hex_code
      ?? variant.hexCode
      ?? variant.color_hex
      ?? variant.colorHex
      ?? variant.swatch_hex
      ?? variant.swatchHex
      ?? variant.color_code
      ?? variant.colorCode
    );
    const label = normalizeColorVariantLabel(variant);
    const optionCode = String(
      variant.color_code
      ?? variant.colorCode
      ?? variant.hex
      ?? variant.hex_code
      ?? variant.hexCode
      ?? variant.color
      ?? variant.color_name
      ?? variant.colorName
      ?? ''
    ).trim();

    if (!label || !optionCode || isNotApplicableColorVariantKey(optionCode)) {
      return;
    }

    const optionKey = normalizeColorMatchToken(rawColorName)
      || normalizeColorMatchToken(optionCode)
      || normalizeColorMatchToken(hex)
      || optionCode;
    if (!optionKey) {
      return;
    }

    const swatchImageUrl = swatchUrlByColorName.get(rawColorName.toLowerCase())
      || resolveRemoteSwatchImageUrl(variant.colorSwatchImg ?? variant.color_swatch_img);
    const inventoryId = sanitizeRemoteInventoryId(variant.id ?? variant.inventory_id ?? variant.inventoryId);
    const variantPrice = Number(variant.price);
    const parsedSortOrder = Number(variant.sort_order ?? variant.sortOrder);
    const sortOrder = Number.isFinite(parsedSortOrder) && parsedSortOrder > 0
      ? parsedSortOrder
      : Number.MAX_SAFE_INTEGER;
    const existingOption = groupedOptions.get(optionKey);

    if (existingOption) {
      if (inventoryId) {
        existingOption.inventoryIds.push(inventoryId);
      }
      if (!existingOption.option.swatchImageUrl && swatchImageUrl) {
        existingOption.option.swatchImageUrl = swatchImageUrl;
      }
      existingOption.sortOrder = Math.min(existingOption.sortOrder, sortOrder);
      return;
    }

    groupedOptions.set(optionKey, {
      option: {
        label,
        hex: hex || PLACEHOLDER_COLOR_HEX,
        code: optionCode,
        ...(Number.isFinite(variantPrice) && variantPrice > 0 ? { price: Number(variantPrice.toFixed(2)) } : {}),
        isPlaceholder: !hex,
        ...(swatchImageUrl ? { swatchImageUrl } : {}),
      },
      inventoryIds: inventoryId ? [inventoryId] : [],
      sortOrder,
    });
  });

  return Array.from(groupedOptions.values())
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map(({ option, inventoryIds }) => {
      const sortedInventoryIds = Array.from(new Set(inventoryIds))
        .sort((left, right) => left.localeCompare(right));

      return {
        ...option,
        ...(sortedInventoryIds.length > 0 ? { inventoryIds: sortedInventoryIds } : {}),
      };
    });
}

function buildRemoteSizeOptions(
  item: RemoteItem,
  category: Exclude<Category, 'All'>,
  sizingOptionsByType: Map<ItemSizingType, string[]>
) {
  const inventoryRows = getInventoryRows(item);
  const sizeRows = inventoryRows.length > 0 ? inventoryRows : [item];
  const presentRowSizes = getPresentRemoteRowSizes(sizeRows, category);

  if (SINGLE_SIZE_CATEGORIES.has(category) && presentRowSizes.length === 0) {
    return ['One Size'];
  }

  const fallbackSizeSystem = normalizeSizingType(getNestedItemField(item, 'size_system'))
    || inferRemoteBottomSizeSystem(item, category);

  if (fallbackSizeSystem) {
    const sizesForSystem = sizingOptionsByType.get(fallbackSizeSystem) ?? [];
    if (sizesForSystem.length > 0) {
      return sizesForSystem;
    }
  }

  const presentSizes = new Map<string, { sortOrder: number | null; conventionIndex: number }>();

  sizeRows.forEach((row) => {
    const normalizedSizeSystem = normalizeSizingType(row.size_system) || fallbackSizeSystem;
    const rowSizeValue = getRemoteRowSizeValue(row, category);
    if (!isAllowedSizingValue(rowSizeValue)) {
      return;
    }

    const conventionIndex = normalizedSizeSystem
      ? (sizingOptionsByType.get(normalizedSizeSystem) ?? []).findIndex((option) => option === rowSizeValue)
      : -1;
    const parsedSortOrder = Number(row.size_sort_order ?? row.sizeSortOrder ?? row.sort_order);
    const sortOrder = Number.isFinite(parsedSortOrder) ? parsedSortOrder : null;
    const existingEntry = presentSizes.get(rowSizeValue);
    const nextEntry = {
      sortOrder,
      conventionIndex: conventionIndex >= 0 ? conventionIndex : Number.MAX_SAFE_INTEGER,
    };

    if (!existingEntry) {
      presentSizes.set(rowSizeValue, nextEntry);
      return;
    }

    const existingSort = existingEntry.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const nextSort = nextEntry.sortOrder ?? Number.MAX_SAFE_INTEGER;
    if (nextSort < existingSort || (nextSort === existingSort && nextEntry.conventionIndex < existingEntry.conventionIndex)) {
      presentSizes.set(rowSizeValue, nextEntry);
    }
  });

  const nextSizeOptions = Array.from(presentSizes.entries())
    .sort(([leftValue, leftMeta], [rightValue, rightMeta]) => {
      if (leftMeta.conventionIndex !== rightMeta.conventionIndex) {
        return leftMeta.conventionIndex - rightMeta.conventionIndex;
      }

      const leftSort = leftMeta.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const rightSort = rightMeta.sortOrder ?? Number.MAX_SAFE_INTEGER;
      if (leftSort !== rightSort) {
        return leftSort - rightSort;
      }

      return leftValue.localeCompare(rightValue, undefined, { numeric: true, sensitivity: 'base' });
    })
    .map(([value]) => value);

  const customSizeIndex = nextSizeOptions.findIndex((value) => value.trim().toLowerCase() === 'custom');
  if (customSizeIndex > 0) {
    const [customSize] = nextSizeOptions.splice(customSizeIndex, 1);
    nextSizeOptions.unshift(customSize);
  }

  return nextSizeOptions;
}

function buildRemoteOptionGroups(
  item: RemoteItem,
  sizeOptions: string[],
  isCustomizable: boolean
): ProductOptionGroup[] | undefined {
  const optionGroups: ProductOptionGroup[] = [];

  if (sizeOptions.length > 0) {
    const normalizedSizeOptions = isCustomizable
      ? [
        'Custom',
        ...sizeOptions.filter((value) => value.trim().toLowerCase() !== 'custom'),
      ]
      : sizeOptions;
    const defaultSizeValue = isCustomizable ? 'Custom' : normalizedSizeOptions[0];
    optionGroups.push({
      key: 'size',
      label: buildLocalizedShopText('Size', '尺码'),
      type: 'button',
      options: normalizedSizeOptions.map((value) => ({
        value,
        label: value,
      })),
      pill: true,
      required: true,
      defaultValue: defaultSizeValue,
    });
  }

  const colorNotApplicable = isRemoteColorVariantsNotApplicable(item);
  const colorOptions = colorNotApplicable ? [] : buildRemoteColorOptions(item);
  if (colorOptions.length > 0) {
    optionGroups.push({
      key: 'color',
      label: buildLocalizedShopText('Color', '颜色'),
      type: 'color',
      presetColors: colorOptions,
      required: true,
      defaultValue: colorOptions[0],
    });
  }

  const fabricCodeOptions = buildRemoteFabricOptions(item);
  // Fabric availability is defined by the item's inventory rows, independently of
  // whether the item supports custom sizing or belongs to a single-size category.
  // Any item with an applicable `fabric_code` must expose that choice on its product page.
  if (fabricCodeOptions.length > 0) {
    optionGroups.push({
      key: 'fabric',
      label: buildLocalizedShopText('Fabric Type', '面料类型'),
      type: 'button',
      options: fabricCodeOptions,
      required: true,
    });
  }

  return optionGroups.length > 0 ? optionGroups : undefined;
}

function buildAdminItemUpdatePayload(
  item: RemoteItem,
  itemId: string,
  title: LocalizedShopText,
  description: LocalizedShopText | null,
  basePrice: number,
  discount: number
) {
  const payload: Record<string, unknown> = {
    item_id: getStringField(item, 'item_id') || itemId,
    gender: getStringField(item, 'gender') || getStringField(item, 'audience'),
    title,
    description: description ?? { en: '', zh: '' },
    category: getStringField(item, 'category'),
    base_price: basePrice,
    discount,
    is_active: item.is_active ?? item.isActive ?? true,
    photos: getRemotePhotoPayload(item),
  };

  const remoteImagesPayload = getRemoteImagesPayload(item);
  if (remoteImagesPayload) {
    payload.images = remoteImagesPayload;
  }

  const optionalKeys = [
    'style',
    'style_code',
    'hs_code',
    'shirt_length',
    'bust',
    'across_back_width',
    'pant_waist',
    'waist',
    'sleeve_length',
    'pant_length',
    'low_hips',
    'hip',
    'inseam',
    'dimensions',
    'inventory_rows',
    'size_system',
    'size_value',
    'stock',
    'images',
    'color_variants',
    'department',
    'item_subcategory',
    'item_subcategory1',
    'item_subcategory2',
    'accessories_size',
    'mid',
    'show_public',
    'season',
    'occasion',
  ];

  optionalKeys.forEach((key) => {
    const value = getNestedItemField(item, key);
    if (value !== undefined && value !== null && value !== '') {
      payload[key] = value;
    }
  });

  if (!payload.mid) {
    const manufacturerId = getNestedItemField(item, 'manufacturer_id');
    if (manufacturerId !== undefined && manufacturerId !== null && manufacturerId !== '') {
      payload.mid = manufacturerId;
    }
  }

  const customOptions = getNormalizedCustomOptions(item);
  if (customOptions) {
    payload.custom_options = customOptions;
  }

  [
    'color_variants',
    'colorVariants',
    'color_options',
    'colorOptions',
    'colors',
    'color_variants_not_applicable',
    'colorVariantsNotApplicable',
    'fabric_codes',
    'fabricCodes',
  ].forEach((key) => {
    const value = getNestedItemField(item, key);
    if (value !== undefined) {
      payload[key] = value;
    }
  });

  if (payload.fabric_codes === undefined && payload.fabricCodes === undefined) {
    const fabricSelections = getRemoteFabricCodeSelections(item);
    if (fabricSelections.length > 0) {
      payload.fabric_codes = Object.fromEntries(fabricSelections);
    }
  }

  return payload;
}

export function toShopProduct(
  item: RemoteItem,
  sizingOptionsByType: Map<ItemSizingType, string[]> = new Map()
): ShopProduct | null {
  if (isRemoteItemDeleted(item)) {
    return null;
  }

  const effectiveSizingOptionsByType =
    sizingOptionsByType.size > 0 ? sizingOptionsByType : readPersistedSizingOptionsByType();
  const id = item.id ?? item.item_id ?? item.itemId;
  const title = normalizeLocalizedShopText(item.title ?? item.name);
  const description = normalizeLocalizedShopText(item.description);
  const category = normalizeRemoteCategory(
    getNestedItemField(item, 'category')
  );
  const image = getPrimaryImageUrl(item);
  const itemLevelPrice = Number(
    getNestedItemField(item, 'base_price')
    ?? getNestedItemField(item, 'basePrice')
    ?? getNestedItemField(item, 'price')
    ?? getNestedItemField(item, 'unit_price')
    ?? getNestedItemField(item, 'unitPrice')
  );
  const remoteItemPrices = getRemoteItemPrices(item);
  const basePrice = remoteItemPrices.length > 0
    ? Math.min(...remoteItemPrices)
    : itemLevelPrice;
  const discount = normalizeDiscountPercentage(getNestedItemField(item, 'discount'));
  const isActive = item.is_active ?? item.isActive;
  const rawStyleCode = item.style ?? item.style_code ?? item.styleCode;
  const styleCode = typeof rawStyleCode === 'string' ? rawStyleCode.trim() : '';
  const itemSubcategory = normalizeItemSubcategory(
    getNestedItemField(item, 'item_subcategory') ?? getNestedItemField(item, 'item_subcategory1')
  );
  const itemSubcategory2 = normalizeItemSubcategory2(getNestedItemField(item, 'item_subcategory2'));
  const accessoriesSize = normalizeAccessoriesSize(getNestedItemField(item, 'accessories_size'));
  const customOptions = getNormalizedCustomOptions(item);
  const isCustomizable = hasCustomSizingOption(customOptions) || CUSTOMIZABLE_STYLE_CODES.has(styleCode);
  const hasCustomOptions = Array.isArray(customOptions) && customOptions.length > 0;

  if (
    !id ||
    !title ||
    !category ||
    !Number.isFinite(basePrice) ||
    basePrice <= 0
  ) {
    return null;
  }

  const stringId = String(id).trim();
  if (!stringId || !title.en.trim()) {
    return null;
  }

  const itemPhotoKeys = Array.from(new Set([
    ...[item.item_photos, item.itemPhotos, item.photos].flatMap((candidate) => {
      return getRemotePhotos(candidate)
        .filter(isRecord)
        .map((photo) => extractItemPhotoObjectKey(photo))
        .filter(Boolean);
    }),
    ...[item.images, item.image_map, item.imageMap].flatMap((candidate) =>
      getRemoteColorKeyedImages(candidate).flatMap((group) =>
        group.photos
          .map((photo) => extractItemPhotoObjectKey(photo))
          .filter(Boolean)
      )
    ),
    ...getInventoryRows(item).flatMap((row) => getInventoryRowPhotoKeys(row)),
  ]));

  const roundedBasePrice = Number(basePrice.toFixed(2));
  const discountedPrice = applyDiscountPercentage(roundedBasePrice, discount);
  const sizes = buildRemoteSizeOptions(item, category, effectiveSizingOptionsByType);
  const optionGroups = buildRemoteOptionGroups(item, sizes, isCustomizable);
  const inventoryRows = getInventoryRows(item);
  const colorImageMap = buildRemoteColorImageMap(item);

  return {
    id: stringId,
    name: title,
    description: description ?? undefined,
    audience: normalizeRemoteAudience(getRemoteAudienceValue(item), category),
    category,
    price: discount > 0 ? discountedPrice : roundedBasePrice,
    ...(discount > 0 ? { originalPrice: roundedBasePrice } : {}),
    ...(discount > 0 ? { discount } : {}),
    image,
    sizes,
    badge: 'New',
    isTryOnReady: true,
    shopKinds: hasCustomOptions ? ['custom'] : ['exclusive'],
    isActive: isActive !== false,
    isCustomizable,
    optionGroups,
    adminItem: true,
    adminItemUpdatePayload: buildAdminItemUpdatePayload(item, stringId, title, description, roundedBasePrice, discount),
    inventoryRows,
    itemPhotoKeys,
    ...(colorImageMap ? { colorImageMap } : {}),
    ...(styleCode ? { styleCode } : {}),
    ...(itemSubcategory ? { itemSubcategory } : {}),
    ...(itemSubcategory2 ? { itemSubcategory2 } : {}),
    ...(accessoriesSize ? { accessoriesSize } : {}),
  };
}

export function buildShopProducts(
  items: RemoteItem[],
  sizingOptionsByType: Map<ItemSizingType, string[]> = new Map()
) {
  const mergedItemsById = new Map<string, RemoteItem>();

  items.forEach((item) => {
    if (isRemoteItemDeleted(item)) {
      return;
    }

    const catalogItemId = getRemoteCatalogItemId(item);
    if (!catalogItemId) {
      return;
    }

    const currentItem = mergedItemsById.get(catalogItemId);
    const currentInventoryRows = currentItem ? getInventoryRows(currentItem) : [];
    const nextInventoryRowsFromItem = getInventoryRows(item);
    const nextItem = currentItem ? { ...currentItem, ...item } : { ...item };
    const fallbackInventoryRow = nextInventoryRowsFromItem.length === 0 ? [item] : [];
    const nextInventoryRows = [...currentInventoryRows, ...nextInventoryRowsFromItem, ...fallbackInventoryRow];
    nextItem.id = catalogItemId;
    nextItem.item_id = catalogItemId;
    nextItem.inventory_rows = nextInventoryRows;
    mergedItemsById.set(catalogItemId, nextItem);
  });

  return Array.from(mergedItemsById.values())
    .map((item) => toShopProduct(item, sizingOptionsByType))
    .filter((product): product is ShopProduct => product !== null);
}

export { getRemoteCatalogItemId };
